import type { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  Input,
} from '@angular/core';
import { PalantirAvatarComponent } from '../../../avatar/avatar.component';
import { PalantirCheckboxComponent } from '../../../checkbox/checkbox.component';
import { PalantirIconComponent } from '../../../icon/icon.component';
import { PalantirListItemComponent } from '../../../list-item/list-item.component';
import { PalantirRadioComponent } from '../../../radio/radio.component';
import { PalantirTagComponent } from '../../../tag/tag.component';
import { PalantirTextComponent } from '../../../text/text.component';
import {
  PalantirSelectItemOverlayItem,
  PalantirSelectItemOverlayType,
} from '../../select-item-overlay.type';

@Component({
  selector: 'palantir-selectable-item-overlay',
  templateUrl: './selectable-item-overlay.component.html',
  styleUrls: ['./selectable-item-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    PalantirListItemComponent,
    PalantirIconComponent,
    PalantirAvatarComponent,
    PalantirTextComponent,
    PalantirCheckboxComponent,
    PalantirRadioComponent,
    PalantirTagComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PalantirSelectableItemOverlayComponent {
  @Input({ required: true }) public item!: PalantirSelectItemOverlayItem;

  @Input({ required: true }) public items!: PalantirSelectItemOverlayItem[];

  @Input({ required: true }) public model!: SelectionModel<number | string>;

  @Input() public type?: PalantirSelectItemOverlayType;

  public limitItems = input<number>(0);

  public constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  public toggleItem() {
    if (this.model.isSelected(this.item.id)) {
      if (this.type === PalantirSelectItemOverlayType.single) {
        this.model.clear();

        return;
      }

      this.deselectItemRecursively(this.item);

      return;
    }

    if (this.type === PalantirSelectItemOverlayType.single) {
      this.model.select(this.item.id);

      return;
    }

    this.selectItemRecursively(this.item);
  }

  public selectItemRecursively(item: PalantirSelectItemOverlayItem) {
    this.model.select(item.id);

    const parent = this.items.find((parentItem) =>
      parentItem.children?.find((child) => child.id === item.id),
    );

    if (!parent) {
      return;
    }

    this.selectItemRecursively(parent);
    this.changeDetectorRef.markForCheck();
  }

  public deselectItemRecursively(item: PalantirSelectItemOverlayItem) {
    this.model.deselect(item.id);

    const children = item.children ?? [];

    for (const child of children) {
      this.deselectItemRecursively(child);
    }

    this.changeDetectorRef.markForCheck();
  }
}
